import React, { useEffect, useRef } from 'react';

const ACCENT = '38, 161, 123';   // #26A17B
const NODE = '61, 219, 169';     // #3ddba9
const CARD_MARGIN = 24;          // margine di rispetto attorno alla scheda
const AREA_PER_NODE = 28000;     // circa 1 nodo ogni 28.000 px quadrati
const PULL = 0.45;               // attrazione fino al 45% della distanza
const EASE = 0.1;                // smorzamento lerp

export const LoginNetwork = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let w = 0, h = 0, dpr = 1, nodes = [], raf = 0, running = true, hole = null, ro = null;
    let linkDist = 170, attractR = 190;
    // puntatore in coordinate del canvas
    const p = { x: 0, y: 0, active: false };

    const cardEl = () => document.querySelector('[data-testid="login-card"]');

    const measureHole = () => {
      const card = cardEl();
      if (!card) { hole = null; return; }
      const host = canvas.getBoundingClientRect();
      const r = card.getBoundingClientRect();
      const radius = parseFloat(getComputedStyle(card).borderRadius) || 18;
      hole = {
        x: r.left - host.left - CARD_MARGIN,
        y: r.top - host.top - CARD_MARGIN,
        w: r.width + CARD_MARGIN * 2,
        h: r.height + CARD_MARGIN * 2,
        r: radius + CARD_MARGIN,
      };
    };

    const seed = () => {
      const isPhone = w < 640;
      const target = Math.round((w * h) / AREA_PER_NODE);
      const min = isPhone ? 18 : 40;
      const max = isPhone ? 40 : 90;
      const count = Math.max(min, Math.min(max, target));
      nodes = Array.from({ length: count }, () => {
        const x = Math.random() * w;
        const y = Math.random() * h;
        return {
          ox: x, oy: y,                                  // orbita
          x, y,                                          // posizione disegnata
          vx: (Math.random() - 0.5) * 0.22,
          vy: (Math.random() - 0.5) * 0.22,
          r: 1 + Math.random() * 1.4,
        };
      });
    };

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.round(rect.width);
      h = Math.round(rect.height);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const diag = Math.hypot(w, h);
      linkDist = Math.max(170, diag * 0.11);
      attractR = Math.max(190, diag * 0.13);
      measureHole();
      seed();
    };

    const punchHole = () => {
      if (!hole) return;
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      ctx.beginPath();
      if (ctx.roundRect) {
        ctx.roundRect(hole.x, hole.y, hole.w, hole.h, hole.r);
      } else {
        ctx.rect(hole.x, hole.y, hole.w, hole.h);
      }
      ctx.fill();
      ctx.restore();
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > linkDist * linkDist) continue;
          const alpha = (1 - Math.sqrt(d2) / linkDist) * 0.3;
          ctx.strokeStyle = `rgba(${ACCENT}, ${alpha.toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      // fili dal puntatore ai nodi vicini
      if (p.active) {
        for (const n of nodes) {
          const d = Math.hypot(n.x - p.x, n.y - p.y);
          if (d > linkDist) continue;
          const alpha = (1 - d / linkDist) * 0.55;
          ctx.strokeStyle = `rgba(${NODE}, ${alpha.toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(n.x, n.y);
          ctx.stroke();
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = `rgba(${NODE}, 0.5)`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      if (p.active) {
        ctx.fillStyle = `rgba(${NODE}, 0.9)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      punchHole();
    };

    const step = () => {
      if (!running) return;
      for (const n of nodes) {
        n.ox += n.vx;
        n.oy += n.vy;
        if (n.ox < 0 || n.ox > w) n.vx *= -1;
        if (n.oy < 0 || n.oy > h) n.vy *= -1;
        let tx = n.ox, ty = n.oy;
        if (p.active) {
          const dx = p.x - n.ox, dy = p.y - n.oy;
          if (Math.hypot(dx, dy) < attractR) {
            tx = n.ox + dx * PULL;
            ty = n.oy + dy * PULL;
          }
        }
        n.x += (tx - n.x) * EASE;
        n.y += (ty - n.y) * EASE;
      }
      draw();
      raf = requestAnimationFrame(step);
    };

    const start = () => {
      if (reduced || raf) return;
      running = true;
      raf = requestAnimationFrame(step);
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const toLocal = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      p.x = clientX - rect.left;
      p.y = clientY - rect.top;
      p.active = p.x >= 0 && p.y >= 0 && p.x <= rect.width && p.y <= rect.height;
    };

    const onPointerMove = (e) => toLocal(e.clientX, e.clientY);
    const onPointerDown = (e) => toLocal(e.clientX, e.clientY);
    const onPointerUp = () => { p.active = false; };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (t) toLocal(t.clientX, t.clientY);
    };
    const onTouchEnd = () => { p.active = false; };
    const onVisibility = () => (document.hidden ? stop() : start());
    const remeasure = () => { measureHole(); if (reduced) draw(); };

    resize();
    draw();
    if (!reduced) start();

    const card = cardEl();
    if (card && window.ResizeObserver) {
      ro = new ResizeObserver(remeasure);
      ro.observe(card);
    }
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', remeasure, { passive: true });
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    window.addEventListener('pointercancel', onPointerUp, { passive: true });
    window.addEventListener('touchstart', onTouchMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('mouseleave', onPointerUp);
    window.addEventListener('blur', onPointerUp);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      if (ro) ro.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', remeasure);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('touchstart', onTouchMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mouseleave', onPointerUp);
      window.removeEventListener('blur', onPointerUp);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="login-network"
      aria-hidden="true"
      data-testid="login-network-canvas"
    />
  );
};

export default LoginNetwork;
